// What the customer does in LaunchDarkly before an account can be checked:
// create an admin's personal access token, and store it in the account's
// vault. The account's setup page shows these steps above the Terraform that
// lets the connector read the token's secret.
//
// The setup steps follow Argo CD's
// (packages/integrations/argocd/frontend/src/preamble.tsx). Harbor's preamble
// shows facts the connector reports, which a LaunchDarkly account stores as
// its generated fields instead.
import { LaunchDarklyComponents } from "@p0security/integrations-launchdarkly-shared/components";
import type { LaunchDarklyAccountConfig } from "@p0security/integrations-launchdarkly-shared/types";
import { Typography } from "@p0security/ui";

const { Paragraph, Text } = Typography;

type VaultKind = LaunchDarklyAccountConfig["vault"]["type"];

const { vault: vaultElement } = LaunchDarklyComponents.account.schema;

/** A vault, as the account's vault field names it. */
export const vaultLabel = (vault: VaultKind) =>
  vaultElement.options[vault].label;

/** The secret, as the setup page names it: by its name, or as the account's
 * own when a stored item has none. */
const secretOf = (secretName: unknown) =>
  typeof secretName === "string" ? (
    <Text code>{secretName}</Text>
  ) : (
    "this account names"
  );

/**
 * The token steps for an account, which the account's setup page shows above
 * its read grant's Terraform: an admin's personal access token with the
 * Admin role, which stops working when that admin leaves.
 */
export const toTokenSteps = (args: {
  secretName: unknown;
  vault: VaultKind;
}) => {
  const { secretName, vault } = args;
  return (
    <Paragraph>
      If you haven&apos;t yet, have a LaunchDarkly admin create a personal
      access token with the Admin role, and store it in {vaultLabel(vault)} as
      the secret {secretOf(secretName)}, holding the token alone. Use an admin
      member set aside for P0 if you can, since the token can do anything that
      admin can. The token stops working when that admin leaves LaunchDarkly,
      and P0 then can&apos;t grant or revoke anything until you store another
      admin&apos;s token in the secret. Then apply the Terraform below so that
      the connector can read the secret.
    </Paragraph>
  );
};
